import debounce from 'lodash.debounce';
import { Socket } from 'socket.io-client';
import { generalEnv } from '@nx-frontend/shared/env';
import { webEditorUpdateCommentsEvent } from '@nx-frontend/shared/store/web-editor-store';
import { NoteCommentsSocketEventType } from '@nx-frontend/shared/types';

interface CommentsObservablePayload {
  eventName: NoteCommentsSocketEventType;
  payload: unknown;
}

interface CommentServiceParams {
  sessionId: string;
  workspaceId: string;
  noteId?: string;
}

interface SendUpdateCommentsEventOriginParams {
  workspaceId?: string | undefined;
  noteId?: string | undefined;
  eventName?: string;
  threadId?: string;
}

interface ISocketMessage {
  [key: string]: string | number | unknown;
}

const compose = <T>(...fns: Array<(a: T) => T>) =>
  fns.reduce(
    (f, g) =>
      (...args) =>
        f(g(...args))
  );

export class CommentService {
  static inited = false;
  static params: CommentServiceParams = {
    sessionId: '',
    workspaceId: '',
    noteId: '',
  };

  static init(socket: Socket, params: CommentServiceParams) {
    CommentService.params = params;
    CommentService.sendUpdateCommentsEventOrigin({});
    if (!CommentService.inited) {
      CommentService.inited = true;
      new CommentService(socket).listen();
    }
  }

  private debounceTimer: NodeJS.Timeout | null = null;

  constructor(private socket: Socket) {}

  public listen() {
    this.socket.on(
      NoteCommentsSocketEventType.COMMENT_CREATE,
      compose(this.sendUpdateComments, this.extractCommentCreateEvent)
    );
    this.socket.on(
      NoteCommentsSocketEventType.COMMENT_UPDATE,
      compose(this.sendUpdateComments, this.extractCommentUpdateEvent)
    );
    this.socket.on(
      NoteCommentsSocketEventType.COMMENT_REMOVE,
      compose(this.sendUpdateComments, this.extractCommentRemoveEvent)
    );
    this.socket.on(
      NoteCommentsSocketEventType.COMMENT_READ,
      compose(this.sendUpdateComments, this.extractCommentReadEvent)
    );
    this.socket.on(
      NoteCommentsSocketEventType.COMMENT_READ_V2,
      compose(this.sendUpdateComments, this.extractCommentReadV2Event)
    );
    this.socket.on(
      NoteCommentsSocketEventType.COMMENT_MENTION,
      compose(this.sendUpdateComments, this.extractCommentMentionEvent)
    );
    this.socket.on(
      NoteCommentsSocketEventType.THREAD_CREATE,
      compose(this.sendUpdateComments, this.extractThreadCreateEvent)
    );
    this.socket.on(
      NoteCommentsSocketEventType.THREAD_UPDATE,
      compose(this.sendUpdateComments, this.extractThreadUpdateEvent)
    );
    this.socket.on(
      NoteCommentsSocketEventType.THREAD_REMOVE,
      compose(this.sendUpdateComments, this.extractThreadRemoveEvent)
    );
  }

  static sendUpdateCommentsEventOrigin(params: SendUpdateCommentsEventOriginParams) {
    const { workspaceId, noteId, sessionId } = CommentService.params;
    webEditorUpdateCommentsEvent({
      gatewayUrl: generalEnv.shortGatewayUrl || generalEnv.gatewayUrl,
      workspaceId: params.workspaceId || workspaceId,
      noteId: params.noteId || (noteId ?? ''),
      sessionId: sessionId,
      eventName: params.eventName,
      threadId: params.threadId,
    });
  }

  private sendUpdateCommentsEvent: Record<
    string,
    { updatedAt: number; fn: (params: SendUpdateCommentsEventOriginParams) => void }
  > = {}; //debounce(CommentService.sendUpdateCommentsEventOrigin, 1000, { trailing: true });

  private getMessageProp<T>(key: string, message: ISocketMessage): T | undefined {
    if (typeof message === 'object' && message && key in message && typeof message[key] === 'string') {
      return message[key] as T;
    }
    return undefined;
  }

  private sendUpdateComments = (params: CommentsObservablePayload) => {
    const { eventName, payload } = params;
    if (typeof payload === 'object' && payload && 'message' in payload) {
      if (typeof payload.message === 'object' && payload.message) {
        const message = payload.message as ISocketMessage;

        const workspaceId = this.getMessageProp<string>('workspaceId', message);
        const noteId =
          this.getMessageProp<string>('noteId', message) || this.getMessageProp<string>('noteGlobalId', message);
        const threadId = this.getMessageProp<string>('threadId', message);

        /**
         * In portal can be more than one page opened, thats why have to listen events for all opened pages
         * below create debounce function for each note, and clear unused debounce functions older than 30 minutes
         * handle all delete events, because we have to remove thread from store
         */

        // create debounce function for each workspaceId + noteId + event
        const debounceKey = `${workspaceId}_${noteId}_${
          eventName === 'comments:threadDeleted' ? 'comments:threadDeleted' : 'event'
        }`;
        if (!this.sendUpdateCommentsEvent[debounceKey]) {
          this.sendUpdateCommentsEvent[debounceKey] = {
            fn: debounce(CommentService.sendUpdateCommentsEventOrigin, 1000, { trailing: true }),
            updatedAt: Date.now(),
          };
        }
        this.sendUpdateCommentsEvent[debounceKey].fn({
          workspaceId,
          noteId,
          eventName,
          threadId,
        });
        this.sendUpdateCommentsEvent[debounceKey].updatedAt = Date.now();

        // clear unused debounce functions older than 1 hour
        if (Object.keys(this.sendUpdateCommentsEvent).length > 20) {
          Object.keys(this.sendUpdateCommentsEvent).forEach((key) => {
            if (key !== debounceKey && this.sendUpdateCommentsEvent[key].updatedAt < Date.now() - 1000 * 60 * 30) {
              delete this.sendUpdateCommentsEvent[key];
            }
          });
        }

        window.__NIMBUS_COMMON.socketEventsSubj$.next({ eventName, payload });
      }
    }
    return { eventName, payload };
  };

  private extractCommentCreateEvent = (eventSource: unknown) => {
    return {
      eventName: NoteCommentsSocketEventType.COMMENT_CREATE,
      payload: eventSource,
    };
  };

  private extractCommentUpdateEvent = (eventSource: unknown) => {
    return {
      eventName: NoteCommentsSocketEventType.COMMENT_UPDATE,
      payload: eventSource,
    };
  };

  private extractCommentRemoveEvent = (eventSource: unknown) => {
    return {
      eventName: NoteCommentsSocketEventType.COMMENT_REMOVE,
      payload: eventSource,
    };
  };

  private extractCommentReadEvent = (eventSource: unknown) => {
    return {
      eventName: NoteCommentsSocketEventType.COMMENT_READ,
      payload: eventSource,
    };
  };

  private extractCommentReadV2Event = (eventSource: unknown) => {
    return {
      eventName: NoteCommentsSocketEventType.COMMENT_READ_V2,
      payload: eventSource,
    };
  };

  private extractCommentMentionEvent = (eventSource: unknown) => {
    return {
      eventName: NoteCommentsSocketEventType.COMMENT_MENTION,
      payload: eventSource,
    };
  };

  private extractThreadCreateEvent = (eventSource: unknown) => {
    return {
      eventName: NoteCommentsSocketEventType.THREAD_CREATE,
      payload: eventSource,
    };
  };

  private extractThreadUpdateEvent = (eventSource: unknown) => {
    return {
      eventName: NoteCommentsSocketEventType.THREAD_UPDATE,
      payload: eventSource,
    };
  };

  private extractThreadRemoveEvent = (eventSource: unknown) => {
    return {
      eventName: NoteCommentsSocketEventType.THREAD_REMOVE,
      payload: eventSource,
    };
  };
}
